import { useEffect, useRef } from "react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";

type ActivityType =
  | "strategy_started"
  | "strategy_completed"
  | "msa_signed"
  | "onboarding_started"
  | "onboarding_complete"
  | "design_approved"
  | "website_launched"
  | "build_order_submitted";

type Activity = {
  activity_type: ActivityType;
  masked_name: string;
  occurred_at: string;
};

const ACTIVITY_COPY: Record<ActivityType, { icon: string; phrase: (n: string) => string }> = {
  strategy_started: { icon: "🎯", phrase: (n) => `${n} just started Strategic Planning` },
  strategy_completed: { icon: "✅", phrase: (n) => `${n} completed their website strategy` },
  msa_signed: { icon: "📝", phrase: (n) => `${n} signed their agreement` },
  onboarding_started: { icon: "🚀", phrase: (n) => `${n} began onboarding` },
  onboarding_complete: { icon: "📦", phrase: (n) => `${n} finished onboarding` },
  design_approved: { icon: "🎨", phrase: (n) => `${n} approved their website design` },
  website_launched: { icon: "🌐", phrase: (n) => `${n} launched their new website` },
  build_order_submitted: { icon: "🛠️", phrase: (n) => `${n} submitted a Build order` },
};

const SocialProofToasts = () => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const feedRef = useRef<Activity[]>([]);
  const cursorRef = useRef(0);

  const loadFeed = async () => {
    const { data, error } = await supabase.rpc("get_public_activity_feed");
    if (error || !data) return;
    feedRef.current = data as Activity[];
    cursorRef.current = 0;
  };

  const showToast = async () => {
    if (feedRef.current.length === 0) {
      await loadFeed();
    }
    const feed = feedRef.current;
    if (feed.length === 0) {
      scheduleNext();
      return;
    }

    const item = feed[cursorRef.current % feed.length];
    cursorRef.current += 1;

    const copy = ACTIVITY_COPY[item.activity_type];
    if (copy) {
      toast({
        title: `${copy.icon} ${copy.phrase(item.masked_name)}`,
        description: formatDistanceToNow(new Date(item.occurred_at), { addSuffix: true }),
        duration: 5000,
      });
    }
    scheduleNext();
  };

  const scheduleNext = () => {
    const delay = Math.floor(Math.random() * (360000 - 120000 + 1)) + 120000;
    timeoutRef.current = setTimeout(showToast, delay);
  };

  useEffect(() => {
    loadFeed();
    const initialDelay = Math.floor(Math.random() * (180000 - 60000 + 1)) + 60000;
    timeoutRef.current = setTimeout(showToast, initialDelay);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default SocialProofToasts;
